import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';
import { describeError, isBenignError } from '../../_helpers/describe-error';

@Injectable({ providedIn: 'root' })
export class MonitoringService {

  private appInsights?: ApplicationInsights;

  /** Global custom properties stamped on every telemetry item (see setGlobalProperty). */
  private globalProperties: Record<string, string> = {};

  /** The App Insights SDK settings. */
  public static sdkConfig(connectionString: string) {
    return {
      connectionString,
      enableCorsCorrelation: true,         // injects traceparent on every HttpClient call
      // Off: headers can carry personal data (a download's file name names the learner), and scrubUrls cleans URLs only.
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
      distributedTracingMode: DistributedTracingModes.W3C,
      // The SDK's own window.onerror capture records a thrown object as "Uncaught [object Object]" with
      // nothing to diagnose it by. The listeners below report the same errors with describeError (bug 40429).
      disableExceptionTracking: true
    };
  }

  constructor(private router: Router) {
    if (!environment.appInsightsConnectionString) {
      return; // graceful no-op when an environment has no App Insights resource
    }

    this.appInsights = new ApplicationInsights({ config: MonitoringService.sdkConfig(environment.appInsightsConnectionString) });

    this.appInsights.loadAppInsights();

    // Errors that escape Angular (GlobalErrorHandler sees the rest), described rather than stringified.
    window.addEventListener('error', (event: ErrorEvent) => this.trackUnhandled(event.error ?? event.message));
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => this.trackUnhandled(event.reason));

    // Browser notices that are not faults are never reported as exceptions (bug 40425).
    this.appInsights.addTelemetryInitializer((envelope) => !MonitoringService.isBenignException(envelope));

    // Set cloud_RoleName on every telemetry item so the workbook "service" column
    // names the environment (Leap, Leap-UAT, Leap-Prod, ...) instead of being blank.
    this.appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags = envelope.tags ?? {};
      envelope.tags['ai.cloud.role'] = environment.appInsightsRoleName;
    });

    // Global custom properties land in customDimensions on every item sent after they are set.
    this.appInsights.addTelemetryInitializer((envelope) => this.stampGlobalProperties(envelope));

    // Query strings can carry search text, names and emails (list filters keep their state in the
    // URL), so no URL leaves the browser with its query or fragment.
    this.appInsights.addTelemetryInitializer((envelope) => MonitoringService.scrubUrls(envelope));

    this.trackPageViews();
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.appInsights!.trackPageView({ uri: MonitoringService.stripQuery(e.urlAfterRedirects) });
      });
  }

  /** The URL without its query string or fragment. */
  static stripQuery(url: string): string {
    if (!url) {
      return url;
    }
    const cut = url.search(/[?#]/);
    return cut === -1 ? url : url.substring(0, cut);
  }

  /**
   * Telemetry initializer: strips query strings and fragments from every URL-bearing field of an item
   * (page view uri/refUri, dependency name/target/data, and the auto-collected page url).
   */
  static scrubUrls(envelope: { baseData?: { [key: string]: any } }): void {
    const data = envelope?.baseData;
    if (!data) {
      return;
    }
    for (const field of ['uri', 'refUri', 'url', 'target', 'data']) {
      if (typeof data[field] === 'string') {
        data[field] = MonitoringService.stripQuery(data[field]);
      }
    }
    // Dependency names are "GET https://host/path?query".
    if (typeof data['name'] === 'string' && /[?#]/.test(data['name'])) {
      data['name'] = MonitoringService.stripQuery(data['name']);
    }
  }

  trackException(error: Error): void {
    this.appInsights?.trackException({ exception: error });
  }

  /** Reports a value thrown outside Angular, described so it can be diagnosed; benign notices are dropped. */
  trackUnhandled(error: unknown): void {
    const asError = error instanceof Error ? error : new Error(describeError(error));
    if (!isBenignError(asError.message)) {
      this.trackException(asError);
    }
  }

  /** Telemetry filter: true for an exception item that is only a benign browser notice (bug 40425). */
  static isBenignException(envelope: { baseType?: string; baseData?: { [key: string]: any } }): boolean {
    if (envelope?.baseType !== 'ExceptionData') {
      return false;
    }
    const exceptions: any[] = envelope.baseData?.['exceptions'] ?? [];
    const messages = exceptions.map(e => e?.message)
      .concat(envelope.baseData?.['message'], envelope.baseData?.['exception']?.message);
    return messages.some(m => isBenignError(m));
  }

  trackEvent(name: string, properties?: Record<string, string>): void {
    this.appInsights?.trackEvent({ name }, properties);
  }

  /**
   * Sets a custom property on every later telemetry item (customDimensions.<name>), replacing any
   * earlier value. Used for the user's feature-switch variant (`features`). A no-op when App Insights
   * is not configured.
   */
  setGlobalProperty(name: string, value: string): void {
    if (!this.appInsights) {
      return;
    }
    this.globalProperties[name] = value;
  }

  /** Telemetry initializer: copies the global properties onto an item's custom properties. */
  stampGlobalProperties(envelope: { data?: { [key: string]: any } }): void {
    const keys = Object.keys(this.globalProperties);
    if (keys.length === 0) {
      return;
    }
    envelope.data = envelope.data ?? {};
    for (const key of keys) {
      envelope.data[key] = this.globalProperties[key];
    }
  }

  /** Call after successful authentication to associate telemetry with the user. */
  setAuthenticatedUser(userId: string): void {
    this.appInsights?.setAuthenticatedUserContext(userId);
  }

  /** Call on logout to clear the authenticated user context. */
  clearAuthenticatedUser(): void {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  /**
   * Current W3C operation (trace) id, for correlating a client-side error with
   * Application Insights telemetry on a support ticket. Null when AI is not configured.
   */
  getOperationId(): string | null {
    return this.appInsights?.context?.telemetryTrace?.traceID ?? null;
  }
}
